import type preact from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { api, ApiError, type Me } from './api.js'
import { Avatar } from './Avatar.js'
import { EingehenderAnruf } from './anruf/Eingehend.js'
import { SosAlarm } from './sos/Eingehend.js'
import { tonScharfmachen } from './ton.js'
import { NAV_ITEMS, SECONDARY_ITEMS, SOS_ITEM, routeLesen } from './nav.js'
import { Anmelden } from './routes/Anmelden.js'
import { Einladung } from './routes/Einladung.js'
import { Start } from './routes/Start.js'
import { direktAktionen } from './start-aktionen.js'
import { lazy, Suspense } from 'preact/compat'
import { Entsperren } from './crypto/Entsperren.js'
import { entsperrschritt, type Autoversuch } from './crypto/entsperrLage.js'
import { STANDARD_ART, type GeheimnisArt } from './crypto/geheimnisArt.js'
import { e2ee, vorwaermen } from './crypto/lazy.js'
// Nur der Typ — zur Laufzeit wird der Speicher weiterhin lazy geladen, sonst
// zöge er die Krypto ins Startbundle (ARCHITECTURE.md §6).
import type * as Keystore from './crypto/keystore.js'
import { WiederherstellungsWarnung } from './crypto/Anker.js'
import { StandortBanner } from './routes/StandortBanner.js'
import { connect, disconnect, onEvent, onReconnect } from './ws.js'
import { profilbilderLaden } from './avatarBilder.js'
import { useOnline } from './useOnline.js'
import { quellenVergessen } from './media/quellen.js'
import { usePrefs } from './prefs.js'
import { t, text, useSprache } from './i18n/t.js'

/**
 * App-Shell.
 *
 * Drei Zustände: Einladung, Anmeldung, angemeldet. Alles andere kommt ab Phase 4
 * als lazy geladene Routen dazu — das Startbudget von 90 kB (ARCHITECTURE.md §6)
 * trägt nur die Shell, die Familienübersicht, den Status und SOS.
 */
export function App() {
  const online = useOnline()
  const perf = usePrefs((s) => s.perf)
  /*
   * Die Sprache steht hier, damit die ganze Hülle sie mitbekommt — und sie
   * dient unten als `key` für den offenen Bereich.
   *
   * `t()` liest die Sprache direkt aus dem Store, nicht über einen Hook. Das
   * ist Absicht: Sonst müssten sechshundert Aufrufstellen einen Hook
   * mitschleppen, und jede vergessene wäre ein Text, der beim Umschalten
   * deutsch stehen bliebe — bis jemand die Seite neu lädt. Ein `key`, der sich
   * ändert, baut den Bereich stattdessen einmal neu auf. Das kostet ein
   * Nachladen der offenen Seite und gilt dafür ausnahmslos.
   */
  const sprache = useSprache()
  const [route, setRoute] = useState(currentHash())
  const [me, setMe] = useState<Me | null | undefined>(undefined)
  /** undefined = noch nicht geprüft, false = gesperrt, true = entsperrt */
  const [entsperrt, setEntsperrt] = useState<boolean | undefined>(undefined)
  const [neuerSpeicher, setNeuerSpeicher] = useState(false)
  /** Ist der Passkey für den Speicher DIESES Geräts als Weg eingerichtet? */
  const [passkeyWeg, setPasskeyWeg] = useState(false)
  /** Satz, Passwort oder PIN — entscheidet, wonach der Entsperr-Bildschirm fragt. */
  const [art, setArt] = useState<GeheimnisArt>(STANDARD_ART)
  /**
   * Steht die Kontrolle an? Dann übergeht die App den Passkey EINMAL und
   * verlangt das Getippte — sonst wäre die Abfrage bloß Zierde.
   */
  const [kontrolle, setKontrolle] = useState(false)
  /**
   * Der automatische Passkey-Versuch beim Start.
   *
   * `offen` → noch nicht versucht, `laeuft` → Zeremonie unterwegs,
   * `fertig` → durch (geklappt oder nicht). **Genau einer je Start**: Ein
   * zweiter nach einem Abbruch wäre eine Schleife, aus der niemand
   * herauskommt.
   */
  const [autoPasskey, setAutoPasskey] = useState<Autoversuch>('offen')
  /**
   * „Schon versucht" — als `ref`, nicht als Zustand.
   *
   * Ein Zustand hier würde eine Neuberechnung auslösen und damit den Effekt,
   * der ihn gerade setzt. Genau daran hing der Ladebildschirm fest.
   */
  const passkeyVersucht = useRef(false)

  /** Vom Passkey geliefertes Geheimnis — entsperrt ohne Passwort. */
  const [prf, setPrf] = useState<ArrayBuffer | null>(null)

  /** Warten, selbst versuchen oder fragen — die Regel steht in `entsperrLage.ts`. */
  const schritt = entsperrschritt({
    prfVorhanden: prf !== null,
    kontrolleFaellig: kontrolle,
    passkeyEingerichtet: passkeyWeg,
    autoVersuch: autoPasskey,
  })

  /*
   * Ton freischalten, sobald jemand die App zum ersten Mal berührt.
   *
   * Muss hier stehen und nicht erst beim Klingeln: Ein `AudioContext`, der
   * außerhalb einer Nutzerhandlung entsteht, bleibt angehalten — und dann
   * bleibt es still, gerade wenn es darauf ankommt. Begründung in `ton.ts`.
   */
  useEffect(() => { tonScharfmachen() }, [])

  useEffect(() => {
    const onHash = () => setRoute(currentHash())
    addEventListener('hashchange', onHash)
    return () => removeEventListener('hashchange', onHash)
  }, [])

  useEffect(() => {
    api.get<Me>('/me')
      .then(setMe)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.needsLogin) setMe(null)
        else setMe(null)
      })
  }, [])

  // Live-Verbindung nur, solange jemand angemeldet ist.
  useEffect(() => {
    if (!me) return
    connect()
    /*
     * Krypto im Hintergrund nachladen, damit der Chat nicht wartet — außer im
     * Sparsam-Modus.
     *
     * Der Krypto-Chunk sind 92 kB gzip, entpackt und geparst ein Vielfaches
     * davon. Wer im Datensparmodus unterwegs ist oder ein Gerät mit 2 GB RAM
     * hat, soll das nicht auf Verdacht bezahlen: Beim Öffnen des Chats wird es
     * ohnehin geladen, dann eben mit einer sichtbaren Wartezeit statt mit
     * einer unsichtbaren Rechnung (Anf. 66/67).
     */
    if (perf !== 'low') vorwaermen()
    return () => disconnect()
  }, [me, perf])

  /*
   * Profilbilder holen — einmal beim Anmelden, danach nur noch, wenn sich in
   * der Familie etwas geändert hat.
   *
   * An EINER Stelle, nicht in jeder Route: Die Avatare stehen auf sechs
   * Bildschirmen, und jede eigene Ladelogik wäre eine Stelle, an der das Bild
   * später fehlt. `family.changed` schickt der Server, sobald jemand sein Bild
   * ändert oder entfernt.
   */
  useEffect(() => {
    if (!me) return
    void profilbilderLaden()
    return onEvent((e) => {
      if (e.type !== 'family.changed') return
      void profilbilderLaden()
      // Der Familienname steht in der Kopfzeile. Benennt jemand die App um,
      // soll sie auf allen Geräten umbenannt sein und nicht erst beim nächsten
      // Neuladen.
      if (e.ref?.['was'] === 'name') void api.get<Me>('/me').then(setMe).catch(() => undefined)
    })
  }, [me?.personId])

  /*
   * Delta-Sync nach einem Verbindungsabbruch (Anf. 30).
   *
   * Der WebSocket meldet nur, was passiert, solange er steht. Nach einem
   * Abbruch fehlt alles dazwischen. Statt beim Reconnect ALLES neu zu laden —
   * auf einer Mobilverbindung mehrere Sekunden für Daten, von denen sich fast
   * nichts geändert hat — holt der Client nur die Lücke.
   *
   * Der Cursor ist die SERVERZEIT aus der letzten Antwort. Die Uhr des
   * Telefons wäre gefährlich: Geht sie vor, würden Änderungen übersprungen.
   */
  useEffect(() => {
    if (!me) return
    return onReconnect(() => {
      const seit = localStorage.getItem('fh-sync-cursor')
      if (!seit) return

      void api.get<{ now: string; truncated: boolean }>(
        `/sync?since=${encodeURIComponent(seit)}`,
      ).then((delta) => {
        localStorage.setItem('fh-sync-cursor', delta.now)
        // Bei zu vielen Änderungen ist ein Delta kein Delta mehr. Dann wird
        // die Seite neu geladen, statt etwas Unvollständiges einzuspielen.
        if (delta.truncated) location.reload()
      }).catch(() => {
        // Klappt der Sync nicht, ist der Cursor unbrauchbar geworden: Beim
        // nächsten Versuch würde eine Lücke entstehen, die niemand bemerkt.
        localStorage.removeItem('fh-sync-cursor')
      })
    })
  }, [me])

  // Beim Anmelden einen Startpunkt setzen — vorher gibt es nichts nachzuholen.
  useEffect(() => {
    if (me) localStorage.setItem('fh-sync-cursor', new Date().toISOString())
  }, [me?.personId])

  // Ist der Schlüsselspeicher auf diesem Gerät schon eingerichtet — und ist der
  // Passkey als Weg hinein hinterlegt?
  useEffect(() => {
    if (!me) { setEntsperrt(undefined); return }
    void (async () => {
      const speicher = await import('./crypto/keystore.js')
      const eingerichtet = await speicher.istEingerichtet()
      setNeuerSpeicher(!eingerichtet)
      setPasskeyWeg((await speicher.wege()).includes('prf'))
      setArt(await speicher.art())
      // Bei einem frischen Speicher gibt es nichts zu kontrollieren — dort wird
      // das Geheimnis in diesem Moment erst festgelegt.
      setKontrolle(eingerichtet && await speicher.kontrolleFaellig())
      // Nach einem Wechsel des Kontos ist der Versuch wieder offen — sonst
      // bliebe er auf „fertig" stehen und der Nächste bekäme den Bildschirm.
      passkeyVersucht.current = false
      setAutoPasskey('offen')
      setEntsperrt(false)
    })()
  }, [me])

  // Hat der Passkey ein Geheimnis geliefert, entsperrt die App ohne Rückfrage.
  // Das ist der geplante Hauptweg (ADR-004): auflegen, drin sein. Klappt es
  // nicht, fällt die App auf die Passphrase zurück, statt hängen zu bleiben.
  useEffect(() => {
    // `kontrolle` schaltet genau hier ab: Das Geheimnis vom Anmelden liegt vor,
    // die App könnte still hineingehen — und tut es an diesem einen Start nicht.
    if (!me || !prf || entsperrt !== false || kontrolle) return
    let abgebrochen = false
    void entsperrenMit('prf', prf, me.personId)
      .then(() => { if (!abgebrochen) setEntsperrt(true) })
      .catch(() => { if (!abgebrochen) setPrf(null) })
    return () => { abgebrochen = true }
  }, [me, prf, entsperrt, kontrolle])

  /*
   * Und derselbe Weg bei einem Start OHNE Anmeldung.
   *
   * ## Der Befund
   *
   * Sitzungen halten 30 Tage; die allermeisten Starts sind Starts ohne
   * Anmeldung, und dann gibt es kein PRF-Geheimnis von oben. Bis hierher
   * bekam Dan deshalb bei **jedem** Öffnen der App einen Bildschirm zu sehen,
   * der ihn bat, auf einen Knopf zu tippen, der dann Face ID auslöste. Der
   * Passkey war da, eingerichtet, funktionsfähig — er wurde nur angeboten
   * statt genommen. Ein Handgriff, der nichts entscheidet.
   *
   * Jetzt versucht die App es von selbst. Der Bildschirm erscheint nur noch,
   * wenn der Versuch nicht durchgeht.
   *
   * ## Warum er trotzdem noch existiert
   *
   * Drei Fälle, in denen er gebraucht wird, und alle drei sind echt:
   *
   *  1. **Die Kontrolle alle vierzehn Tage.** Dann wird bewusst nicht
   *     versucht — genau das ist ihr Zweck (Dan: „diese abfrage alle 2
   *     wochen").
   *  2. **Kein Passkey für diesen Speicher**, oder ein Gerät, das kein PRF
   *     liefert. Dann bleibt nur das Getippte.
   *  3. **Die Zeremonie kommt nicht zustande.** Safari verlangt für WebAuthn
   *     je nach Fassung eine Nutzerhandlung, und ein Seitenaufbau ist keine.
   *     Dann fällt die App auf den Knopf zurück, statt hängen zu bleiben —
   *     der Knopf IST die Nutzerhandlung.
   */
  useEffect(() => {
    if (!me || entsperrt !== false) return
    if (schritt !== 'passkey-versuchen') return
    if (passkeyVersucht.current) return

    /*
     * ## Warum hier ein `ref` steht und KEINE Aufräumfunktion
     *
     * Die erste Fassung hatte beides falsch herum und hat Dan aus seiner
     * eigenen App ausgesperrt: „hängt im bildschirm: Wird geladen…"
     *
     * Sie merkte sich den Abbruch in einer Aufräumfunktion und hängte den
     * Effekt an `schritt`. Nur ist `schritt` aus `autoPasskey` gerechnet —
     * die erste Zeile des Effekts (`'laeuft'`) änderte also seine eigene
     * Abhängigkeit. Preact räumte auf, setzte `abgebrochen`, und das
     * `finally` durfte danach weder `'fertig'` noch `setEntsperrt` schreiben.
     * Ergebnis: `warten`, für immer, ohne Fehlermeldung — der Ladebildschirm
     * stand still, während die Zeremonie längst durch war.
     *
     * Ein `ref` hält „schon versucht" fest, ohne eine Neuberechnung
     * auszulösen. Und es gibt nichts abzuräumen: Dieses Bauteil ist die
     * App-Hülle, sie wird nicht ausgehängt, solange jemand angemeldet ist.
     */
    passkeyVersucht.current = true
    setAutoPasskey('laeuft')

    /*
     * Eine Notbremse für den Ladebildschirm.
     *
     * Die Zeremonie holt zuerst eine Challenge vom Server, und `api` kennt
     * keine Zeitgrenze. Ein Netz, das nicht antwortet statt abzulehnen —
     * schlechter Empfang, ein hängender Tunnel — ließe das Versprechen
     * „gleich fertig" nie einlösen, und es stünde wieder „Wird geladen…".
     *
     * Nach fünfzehn Sekunden erscheint deshalb der Entsperr-Bildschirm.
     * Abgebrochen wird dabei NICHTS: Kommt die Zeremonie doch noch durch,
     * entsperrt sie trotzdem. Die Bremse betrifft nur die Anzeige.
     */
    const bremse = setTimeout(() => setAutoPasskey('fertig'), 15_000)

    void (async () => {
      try {
        const pk = await import('./crypto/passkeyEntsperren.js')
        if (!pk.passkeysMoeglich() || pk.prfAussichtslos()) return

        const geheimnis = await pk.passkeyGeheimnis()
        if (!geheimnis) { pk.prfAussichtslosMerken(); return }

        await entsperrenMit('prf', geheimnis, me.personId)
        setEntsperrt(true)
      } catch {
        // Abgebrochen, verweigert, keine Nutzerhandlung — in allen Fällen
        // gehört jetzt der Bildschirm hin, nicht ein zweiter Versuch.
      } finally {
        // Muss IMMER laufen. Solange das ausbleibt, steht der Ladebildschirm.
        clearTimeout(bremse)
        setAutoPasskey('fertig')
      }
    })()
  }, [me, entsperrt, schritt])

  const einladungsToken = route.startsWith('/einladung/') ? route.slice('/einladung/'.length) : null

  if (einladungsToken) {
    return <Einladung token={einladungsToken} onDone={(m) => { setMe(m); location.hash = '#familie' }} />
  }
  if (me === undefined) return <Startbildschirm />
  if (me === null) {
    return (
      <Anmelden onDone={(m, prfGeheimnis) => {
        setPrf(prfGeheimnis ?? null)
        setMe(m)
        location.hash = '#familie'
      }} />
    )
  }

  // Der Schlüsselspeicher wird beim ersten Öffnen entsperrt. Ohne ihn liefe der
  // Chat blind — deshalb kommt der Schritt vor der Oberfläche, nicht danach.
  if (entsperrt === false) {
    // Solange etwas läuft, das gleich still entsperrt, darf hier nichts
    // aufblitzen — weder das Geheimnis aus der Anmeldung noch die eigene
    // Zeremonie. Bei fälliger Kontrolle gibt `entsperrschritt` „fragen".
    if (schritt !== 'fragen') return <Startbildschirm />

    return (
      <Entsperren
        zweck={neuerSpeicher ? 'neu' : kontrolle ? 'kontrolle' : 'entsperren'}
        art={art}
        onArt={setArt}
        passkeyWeg={passkeyWeg}
        mitGeheimnis={async (eingabe) => {
          await entsperrenMit('passphrase', eingabe, me.personId)
          const speicher = await import('./crypto/keystore.js')
          // Bei der ersten Einrichtung hält der Speicher fest, wonach künftig
          // gefragt wird. Ohne das stünde beim nächsten Start wieder „Satz",
          // auch wenn gerade eine PIN vergeben wurde.
          if (neuerSpeicher) await speicher.artSetzen(art)
          // Und in jedem Fall: Die Uhr für die nächste Kontrolle beginnt neu.
          // Auch beim gewöhnlichen Entsperren — wer ohnehin tippt, muss in
          // vierzehn Tagen nicht noch einmal geprüft werden.
          else await speicher.kontrolleErledigt()
          setKontrolle(false)
        }}
        mitPasskey={async () => {
          const { passkeyGeheimnis } = await import('./crypto/passkeyEntsperren.js')
          const geheimnis = await passkeyGeheimnis()
          if (!geheimnis) return false
          await entsperrenMit('prf', geheimnis, me.personId)
          return true
        }}
        // Läuft auf einem bereits offenen Speicher: Der Speicherschlüssel wird
        // ein zweites Mal verschlüsselt, diesmal mit dem Geheimnis des Passkeys.
        // Die Inhalte bleiben unberührt, der Satz gilt weiter.
        passkeyNachruesten={async () => {
          const { passkeyGeheimnis } = await import('./crypto/passkeyEntsperren.js')
          const { wegHinzufuegen } = await import('./crypto/keystore.js')
          const geheimnis = await passkeyGeheimnis()
          if (!geheimnis) return false
          await wegHinzufuegen('prf', geheimnis)
          return true
        }}
        /*
         * Der Ausweg aus der Kontrolle: drei Tage Aufschub, dann per Passkey
         * hinein.
         *
         * Erst aufschieben, dann entsperren — bricht die Zeremonie ab, steht
         * der Aufschub trotzdem. Sonst käme dieselbe Abfrage beim nächsten
         * Start sofort wieder, und der Ausweg wäre keiner.
         *
         * Das Geheimnis vom Anmelden wird wiederverwendet, wenn es vorliegt.
         * Eine zweite Zeremonie wäre nicht falsch, nur überflüssig: Der Finger
         * lag vor wenigen Sekunden schon auf.
         */
        onAufschieben={async () => {
          const speicher = await import('./crypto/keystore.js')
          await speicher.kontrolleAufschieben()
          setKontrolle(false)

          if (prf) {
            await entsperrenMit('prf', prf, me.personId)
            return true
          }
          const { passkeyGeheimnis } = await import('./crypto/passkeyEntsperren.js')
          const geheimnis = await passkeyGeheimnis()
          if (!geheimnis) return false
          await entsperrenMit('prf', geheimnis, me.personId)
          return true
        }}
        onFertig={() => setEntsperrt(true)}
        // Der Notausgang. Ohne ihn ist der Entsperr-Bildschirm eine Sackgasse:
        // Er riegelt die App ab, bevor irgendein Menü erreichbar ist — wer den
        // Satz vergisst, kommt an den Abmelden-Knopf gar nicht mehr heran.
        // Aufgefallen beim ersten echten Gebrauch, nicht beim Lesen.
        onVergessen={async () => {
          await abmeldenUndLeeren()
          setEntsperrt(undefined)
          setMe(null)
        }}
      />
    )
  }
  if (entsperrt === undefined) return <Startbildschirm />

  const { bereich: roh, raum } = routeLesen(route)
  // Leerer Hash heißt „Startseite", nicht „unbekannter Bereich". Ohne diese
  // Zeile stand beim ersten Öffnen die Überschrift „Familie" doppelt da: einmal
  // als Reiter, einmal als Titel — und kein Reiter war hervorgehoben.
  const bereich = roh || NAV_ITEMS[0].id
  const unterbereich = SECONDARY_ITEMS.find((i) => i.id === bereich)
  const tab = NAV_ITEMS.find((i) => i.id === bereich)
  /*
   * SOS ist kein Reiter mehr, aber sehr wohl ein Bereich. Ohne diese Zeile
   * fiele der Alarmbildschirm auf die Familienseite zurück — der Knopf wäre
   * da und würde nichts öffnen.
   */
  const offenerBereich = tab?.id ?? unterbereich?.id ?? (bereich === SOS_ITEM.id ? SOS_ITEM.id : NAV_ITEMS[0].id)
  const aktiverTab = tab ?? (unterbereich ? NAV_ITEMS.find((i) => i.id === 'mehr')! : undefined)
  const titel = unterbereich?.label
    ?? (bereich === SOS_ITEM.id ? text('SOS') : undefined)
    ?? tab?.label
    ?? NAV_ITEMS[0].label

  return (
    <div class="fh-app">
      <header class="fh-bar">
        {/*
          Oben steht der Name der FAMILIE, nicht der der App.

          Dan: „ich möchte family hub frei wählbar haben." Er hat recht — ein
          Produktname in der Kopfzeile ist die Handschrift eines Herstellers,
          und der ist hier niemand. Die App gehört einer bestimmten Familie,
          und wie die heißt, weiß nur sie.

          „Family Hub" bleibt der Rückfall, solange nichts gesetzt ist. Ändern
          lässt es sich unter Einstellungen — mit `family.manage`, denn es
          steht auf jedem Bildschirm jedes Mitglieds.
        */}
        <h2 class="fh-bar__titel">{me.familyName || t('Family Hub')}</h2>
        {/*
          SOS steht in der Kopfzeile, zwischen Namen und Lupe — auf jedem
          Bildschirm, in keinem Menü, nicht wegscrollbar (Anf. 28/61).

          Vorher schwebte er unten rechts, im Daumenbereich. Der Tausch ist
          bewusst: Dort verdeckte er beim Scrollen gelegentlich einen Knopf
          darunter (etwa „Foto" in der letzten Familienzeile). Hier verdeckt er
          nie etwas, dafür ist er einhändig schlechter zu erreichen. Rot bleibt
          rot, das Wort bleibt im Knopf — die Farbe trägt die Bedeutung nicht
          allein, und `aria-label` sagt, was passiert.
        */}
        <a
          class="fh-sos-knopf"
          href={`#${SOS_ITEM.id}`}
          aria-label={t('SOS — Notfall auslösen')}
          aria-current={bereich === SOS_ITEM.id ? 'page' : undefined}
        >
          <span aria-hidden="true">{SOS_ITEM.icon}</span>
          <span aria-hidden="true">{t(SOS_ITEM.label)}</span>
        </a>
        {/*
          Lupe und Menü zusammen in einer Gruppe: Bei großer Schrift bricht die
          Leiste um, und ohne diese Klammer stünde das Dreipunktmenü allein in
          der zweiten Zeile — das sieht aus wie ein Fehler, nicht wie Absicht.
        */}
        <div class="fh-bar__aktionen">
          <a class="fh-bar__aktion" href="#suche" title={t('Suchen')}>
            <span aria-hidden="true">🔍</span>
            <span class="fh-visually-hidden">{t('Suchen')}</span>
          </a>
          <a class="fh-bar__aktion" href="#mehr" title={t('Mehr')}>
            <span aria-hidden="true">⋮</span>
            <span class="fh-visually-hidden">{t('Mehr')}</span>
          </a>
        </div>
      </header>

      <nav class="fh-tabs" aria-label={t('Hauptbereiche')}>
        {NAV_ITEMS.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            class="fh-tab"
            aria-current={item.id === aktiverTab?.id ? 'page' : undefined}
          >
            <span class="fh-tab__icon" aria-hidden="true">{item.icon}</span>
            <span>{t(item.label)}</span>
          </a>
        ))}
      </nav>

      <div class="fh-banner-stapel">
        {/*
          Ein klingelndes Telefon steht ganz oben — vor jeder Warnung.

          Und es steht HIER, in der Hülle, nicht im Anrufbereich. Bis zum
          2026-09-10 hing der Zuhörer für `call.incoming` in `routes/Anruf.tsx`
          und damit an der Bedingung, dass man gerade auf dem Reiter „Anrufe"
          steht. Wer im Chat war oder auf der Familienseite, bekam von einem
          Anruf nichts mit — von Gerät zu Gerät ging deshalb praktisch nie
          einer durch. Wer das zurück in den Anrufbereich schiebt, baut den
          Fehler wieder ein.
        */}
        {/*
          Ein SOS steht über allem — auch über einem klingelnden Telefon.
          Wer gerade angerufen wird und gleichzeitig einen Alarm bekommt,
          soll den Alarm zuerst sehen.
        */}
        <SosAlarm meId={me.personId} />

        <EingehenderAnruf meId={me.personId} />

        {/*
          Steht VOR dem Offline-Banner: Eine laufende Wiederherstellung ist das
          Wichtigste, was die App jemand mitteilen kann (ADR-008, Risiko T13).
        */}
        <WiederherstellungsWarnung />

        {/*
          Der Standort-Banner steht HIER und nicht im Standort-Bereich. Wer ihn
          dorthin verschiebt, macht ihn unsichtbar, sobald jemand im Chat ist —
          und hebt damit ADR-009, Bedingung 3 auf.
        */}
        <StandortBanner />

        {!online && (
          <div class="fh-banner fh-banner--offline" role="status">
            {/* Symbol UND Text — Farbe trägt die Information nie allein (Anf. 58). */}
            <span aria-hidden="true">🟠</span>
            <span>{t('Keine Internetverbindung. Du siehst gespeicherte Inhalte.')}</span>
          </div>
        )}
      </div>

      <main class="fh-main" id="inhalt" tabIndex={-1}>
        {/*
          Die Überschrift bleibt im Dokument, wird aber unsichtbar, wenn der
          Reiter oben schon dasselbe Wort trägt: Zweimal „Chats" untereinander
          ist keine Orientierung, sondern Wiederholung. Für Bereiche ohne
          eigenen Reiter — Fotos, Dateien, Kalender — steht sie sichtbar da.
        */}
        <h1 class={tab ? 'fh-visually-hidden' : 'fh-h1'}>{t(titel)}</h1>
        <Bereich
          key={sprache}
          route={offenerBereich}
          raum={raum}
          me={me}
          onLoggedOut={() => setMe(null)}
          onProfilGeaendert={() => {
            void api.get<Me>('/me').then(setMe).catch(() => undefined)
          }}
        />
      </main>

    </div>
  )
}

/*
 * Nachgeladene Bereiche.
 *
 * Der Chat stand bis Phase 14 versehentlich im Startbundle: Er war statisch
 * importiert, also lag sein Code in `index-*.js`. Das Budget „Chat ≤ 60 kB"
 * fand deshalb keinen einzigen Chunk — und meldete genau deswegen „ok". Ein
 * Budget, das nichts findet, prüft nichts; seit Phase 14 lässt der leere
 * Treffer die Prüfung scheitern (scripts/check-bundle-budget.mjs).
 */
const Chat = lazy(() => import('./routes/Chat.js').then((m) => ({ default: m.Chat })))
const Mitglieder = lazy(() =>
  import('./routes/Mitglieder.js').then((m) => ({ default: m.Mitglieder })))
const Geraete = lazy(() => import('./routes/Geraete.js').then((m) => ({ default: m.Geraete })))
const Dateien = lazy(() => import('./routes/Dateien.js').then((m) => ({ default: m.Dateien })))
const Anruf = lazy(() => import('./routes/Anruf.js').then((m) => ({ default: m.Anruf })))
const Standort = lazy(() =>
  import('./routes/Standort.js').then((m) => ({ default: m.Standort })))
const Status = lazy(() => import('./routes/Status.js').then((m) => ({ default: m.Status })))
const Sos = lazy(() => import('./routes/Sos.js').then((m) => ({ default: m.Sos })))
const Kalender = lazy(() =>
  import('./routes/Module.js').then((m) => ({ default: m.Kalender })))
const Aufgaben = lazy(() =>
  import('./routes/Module.js').then((m) => ({ default: m.Aufgaben })))
const Notizen = lazy(() =>
  import('./routes/Module.js').then((m) => ({ default: m.Notizen })))
const Fotos = lazy(() => import('./routes/Fotos.js').then((m) => ({ default: m.Fotos })))
const Suche = lazy(() => import('./routes/Suche.js').then((m) => ({ default: m.Suche })))
const Einstellungen = lazy(() =>
  import('./routes/Einstellungen.js').then((m) => ({ default: m.Einstellungen })))

function Bereich({ route, raum, me, onLoggedOut, onProfilGeaendert }: {
  route: string
  /** Aus `#chat/<roomId>`: der Raum, der direkt geöffnet werden soll. */
  raum: string | null
  me: Me
  onLoggedOut: () => void
  /** Profilbild oder Familienname wurden geändert — `/me` neu holen. */
  onProfilGeaendert: () => void
}) {
  if (route === 'dateien') {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        <Dateien />
      </Suspense>
    )
  }

  if (route === 'familie') {
    // Die Startseite ist NICHT lazy: Sie ist das Erste, was jemand sieht, und
    // ein Nachladen davor wäre genau die Wartezeit, die vermieden werden soll
    // (ARCHITECTURE.md §6).
    return <Start me={me} aktionen={direktAktionen()} />
  }

  // Die Module aus Phase 12. Alle lazy: Wer nie einen Termin anlegt, lädt
  // den Kalender nie (ARCHITECTURE.md §6).
  const module: Record<string, preact.JSX.Element> = {
    chat: <Chat me={me} oeffne={raum} />,
    mitglieder: <Mitglieder me={me} />,
    kalender: <Kalender me={me} />,
    aufgaben: <Aufgaben />,
    notizen: <Notizen />,
    fotos: <Fotos me={me} />,
    suche: <Suche />,
    einstellungen: (
      <Einstellungen me={me} onGeloescht={onLoggedOut} onProfilGeaendert={onProfilGeaendert} />
    ),
  }
  if (module[route]) {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        {module[route]}
      </Suspense>
    )
  }

  if (route === 'sos') {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        <Sos me={me} />
      </Suspense>
    )
  }

  if (route === 'status') {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        <Status me={me} />
      </Suspense>
    )
  }

  if (route === 'standort') {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        <Standort me={me} />
      </Suspense>
    )
  }

  if (route === 'anrufen') {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        <Anruf me={me} />
      </Suspense>
    )
  }

  if (route === 'mehr') {
    return (
      <>
        {/*
          „Mehr" ist eine Liste von Zeilen, keine Wand aus Karten — dieselbe
          Zeile wie in der Familienübersicht und im Chat. Wer eine Einstellung
          sucht, soll nicht erst herausfinden, welche Sorte Kachel er vor sich
          hat.
        */}
        <ul class="fh-zeilen">
          <li>
            <div class="fh-zeile">
              <Avatar name={me.displayName} person={me.personId} />
              <span class="fh-zeile__mitte">
                <span class="fh-zeile__name">{me.displayName}</span>
                <span class="fh-zeile__unter">{t(rollenName(me.role))}</span>
              </span>
            </div>
          </li>
          {SECONDARY_ITEMS.map((item) => (
            <li key={item.id}>
              <a class="fh-zeile" href={`#${item.id}`}>
                <span class="fh-avatar" aria-hidden="true">{item.icon}</span>
                <span class="fh-zeile__mitte">
                  <span class="fh-zeile__name">{t(item.label)}</span>
                </span>
                <span class="fh-zeile__rechts" aria-hidden="true">›</span>
              </a>
            </li>
          ))}
        </ul>

        <h2 class="fh-h2">{t('Meine Geräte')}</h2>
        <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
          <Geraete onLoggedOut={onLoggedOut} />
        </Suspense>

        <h2 class="fh-h2">{t('Sicherheit')}</h2>
        <SicherheitsBereich me={me} />

        <div class="fh-card">
          <button
            class="fh-btn fh-btn--gefahr"
            onClick={() => {
              // Beim Abmelden wird der Schlüsselspeicher geleert, nicht nur der
              // Schlüssel vergessen (SECURITY.md §3.5).
              void abmeldenUndLeeren().then(onLoggedOut)
            }}
          >
            {t('Abmelden')}
          </button>
        </div>
      </>
    )
  }

  return (
    <div class="fh-card">
      <h2 class="fh-h2">{t('Noch nicht gebaut')}</h2>
      <p class="fh-muted">
        {t('Diesen Bereich gibt es noch nicht. Das Gerüst steht, die Funktion noch nicht.')}
      </p>
    </div>
  )
}

/**
 * Abmelden und den Schlüsselspeicher LEEREN — nicht nur den Schlüssel vergessen
 * (SECURITY.md §3.5).
 *
 * Steht bewusst an einer Stelle für beide Aufrufer: den Abmelden-Knopf und den
 * Notausgang „Satz vergessen". Zwei Kopien dieser Reihenfolge würden
 * auseinanderdriften, und die Stelle, an der etwas übrig bliebe, wäre die
 * unangenehmste denkbare.
 */
async function abmeldenUndLeeren(): Promise<void> {
  await api.post('/auth/logout').catch(() => {
    // Auch ohne erreichbaren Server muss das Gerät sauber werden. Die Sitzung
    // läuft dann serverseitig ab, statt sofort zu enden — das lokale
    // Schlüsselmaterial ist trotzdem weg.
  })
  // Signierte Bild-URLs hängen an der Sitzung. Nach einem Kontowechsel auf
  // demselben Gerät wären sie wertlos — der Zwischenspeicher wüsste es nur nicht.
  quellenVergessen()
  const keystore = await import('./crypto/keystore.js')
  await keystore.loeschen()
  // Der Service Worker hält seit Phase 13 einen Daten-Cache. Er gehört zum
  // selben Aufräumen wie der Schlüsselspeicher: Das Abmelden ist der Moment,
  // in dem ein Gerät die Hand wechseln könnte.
  navigator.serviceWorker?.controller?.postMessage({ type: 'fh-abmelden' })
}

/**
 * Entsperrt den Schlüsselspeicher und startet die Krypto.
 *
 * Beide Wege enden hier: Passkey-Geheimnis (PRF) oder Passphrase. Der
 * Unterschied liegt nur im ersten Argument — alles danach ist gleich.
 */
async function entsperrenMit(
  methode: 'prf' | 'passphrase',
  geheimnis: ArrayBuffer | string,
  personId: string,
): Promise<void> {
  const speicher = await import('./crypto/keystore.js')
  await speicher.entsperren(methode, geheimnis)

  const krypto = await e2ee()
  const geraete = await api.get<Array<{ id: string; isCurrent: boolean }>>('/devices')
  const eigenes = geraete.find((d) => d.isCurrent) ?? geraete[0]
  if (eigenes) await krypto.starten(personId, eigenes.id)

  await recoverySchluesselSicherstellen(speicher)
}

/**
 * Legt einmalig das Recovery-Schlüsselpaar dieses Geräts an.
 *
 * Ohne den öffentlichen Teil kann niemand einen Anker-Anteil FÜR uns
 * verschlüsseln — die Familie sähe uns in der Auswahl gar nicht erst. Der
 * private Teil bleibt im versiegelten Speicher; der Server bekommt ihn nie.
 *
 * Scheitert der Netzaufruf, ist das kein Grund, die Anmeldung abzubrechen:
 * beim nächsten Entsperren versucht es die App erneut.
 */
async function recoverySchluesselSicherstellen(
  speicher: typeof Keystore,
): Promise<void> {
  try {
    if (await speicher.holen('recovery-keypair')) return
    const { verwahrerSchluesselpaar } = await import('@fh/crypto')
    const paar = await verwahrerSchluesselpaar()
    const privat = new Uint8Array(await crypto.subtle.exportKey('pkcs8', paar.privateKey))
    await api.put('/recovery/public-key', { publicKey: paar.publicKey })
    await speicher.ablegen('recovery-keypair', { publicKey: paar.publicKey, privateKey: privat })
  } catch {
    // Nächster Versuch beim nächsten Entsperren.
  }
}

/**
 * Der Sicherheitsbereich lädt Anker und Sicherheitsnummer nach — beide
 * brauchen die Krypto-Bibliothek und haben im Startbundle nichts verloren.
 */
function SicherheitsBereich({ me }: { me: Me }) {
  const [Inhalt, setInhalt] = useState<preact.ComponentType<{ me: Me }> | null>(null)

  useEffect(() => {
    void import('./crypto/SicherheitsBereich.js').then((m) => setInhalt(() => m.SicherheitsBereich))
  }, [])

  if (!Inhalt) return <p class="fh-muted">{t('Wird geladen…')}</p>
  return <Inhalt me={me} />
}

/** Kein leerer Bildschirm, während `/me` läuft (Anf. 71). */
function Startbildschirm() {
  return (
    <div class="fh-anmelden">
      <p class="fh-muted">{t('Wird geladen…')}</p>
    </div>
  )
}

function rollenName(role: string): string {
  const namen: Record<string, string> = {
    admin: text('Administrator'), parent: text('Elternteil'), adult: text('Erwachsener'),
    child: text('Kind'), guest: text('Gast'),
  }
  return namen[role] ?? role
}

function currentHash(): string {
  return location.hash.replace(/^#/, '')
}
